import { routePath } from './strings/RoutePath';

/**
 * Post-login redirection is deliberately confined to three places:
 *
 *   1. `PrivateRoute` puts the attempted location in the login URL as `redirect`.
 *   2. `DashboardLayout` captures that parameter into session storage, then
 *      consumes it once the user is authenticated and has landed somewhere.
 *   3. `signinCleanUp` drops anything left over on sign-out.
 *
 * Nothing in the authentication flow itself — the sagas, the formik hooks, the
 * challenge screens — needs to know this mechanism exists. They keep navigating
 * to the dashboard exactly as they always have, and the capture/consume pair
 * hands the user on from there. Adding a new route or a new auth screen requires
 * no awareness of any of this.
 */

/**
 * Name of the search parameter used to advertise a user's intended destination
 * on the login URL.
 */
export const REDIRECT_PARAM = 'redirect';

const STORAGE_KEY = 'pendingRedirect';

/**
 * Routes that are part of the authentication flow itself. Using one of these as
 * a post-login destination would either bounce the user straight back into the
 * flow or loop, so they are never accepted as a redirect target. The list is
 * also what tells the consumer that a user is still mid-flow and should be left
 * alone.
 */
const AUTH_ROUTES = [
    routePath.login,
    routePath.forgotpassword,
    routePath.confirmforgotpassword,
    routePath.updatepassword,
    routePath.codechallenge,
    routePath.kycSubmission,
    routePath.register,
    routePath.registerRedirect,
];

/**
 * True while the given path is one of the authentication screens.
 *
 * @param {string | null | undefined} pathname
 * @returns {boolean}
 */
export const isAuthRoute = (pathname) => AUTH_ROUTES.includes(pathname);

/**
 * Validates a redirect value before it is handed to the router.
 *
 * Only same-origin, root-relative locations are allowed. Anything else — an
 * absolute URL, a protocol-relative path (`//evil.example`), a backslash
 * variant that the URL parser normalises into one, or a non-path string — is
 * rejected so the value cannot be used as an open-redirect vector.
 *
 * @param {string | null | undefined} value
 * @returns {string | null} A safe `pathname + search + hash`, or null.
 */
export const sanitizeRedirect = (value) => {
    if (!value || typeof value !== 'string') {
        return null;
    }

    if (!value.startsWith('/')) {
        return null;
    }

    let url;

    try {
        url = new URL(value, window.location.origin);
    } catch (err) {
        return null;
    }

    if (url.origin !== window.location.origin) {
        return null;
    }

    const path = `${url.pathname}${url.search}${url.hash}`;

    // A protocol-relative path handed to history() would navigate off-origin.
    if (!path.startsWith('/') || path.startsWith('//')) {
        return null;
    }

    if (isAuthRoute(url.pathname)) {
        return null;
    }

    return path;
};

/**
 * Reads and validates the redirect target out of a location search string.
 *
 * @param {string | null | undefined} search - e.g. location.search
 * @returns {string | null}
 */
export const getRedirectTarget = (search) => {
    if (!search) {
        return null;
    }

    return sanitizeRedirect(new URLSearchParams(search).get(REDIRECT_PARAM));
};

/**
 * Remembers where the user was trying to go, so the destination survives the
 * whole authentication flow — including detours through a forced password
 * change or a KYC submission — without being threaded through it.
 *
 * Session storage keeps this per-tab and clears itself when the tab closes.
 *
 * @param {string | null | undefined} target
 */
export const storePendingRedirect = (target) => {
    const safe = sanitizeRedirect(target);

    if (!safe) {
        return;
    }

    try {
        window.sessionStorage.setItem(STORAGE_KEY, safe);
    } catch (err) {
        // Storage can be unavailable (private browsing, quota). Losing the
        // destination just means the user lands on the dashboard.
    }
};

/**
 * Returns the stored destination and removes it, so a destination is only ever
 * acted on once.
 *
 * @returns {string | null}
 */
export const consumePendingRedirect = () => {
    let stored = null;

    try {
        stored = window.sessionStorage.getItem(STORAGE_KEY);
        window.sessionStorage.removeItem(STORAGE_KEY);
    } catch (err) {
        return null;
    }

    return sanitizeRedirect(stored);
};

/**
 * Drops any stored destination. Called on sign-out so a destination captured but
 * never reached cannot resurface in a later session.
 */
export const clearPendingRedirect = () => {
    try {
        window.sessionStorage.removeItem(STORAGE_KEY);
    } catch (err) {
        // Nothing to do.
    }
};

/**
 * Builds the login path for an unauthenticated user, advertising where they were
 * trying to go so it is visible in the URL and survives a manual reload.
 *
 * @param {{ pathname?: string; search?: string; hash?: string }} location
 * @returns {string}
 */
export const buildLoginPath = (location) => {
    const intended = `${location?.pathname ?? ''}${location?.search ?? ''}${
        location?.hash ?? ''
    }`;

    const target = sanitizeRedirect(intended);

    if (!target) {
        return routePath.login;
    }

    return `${routePath.login}?${REDIRECT_PARAM}=${encodeURIComponent(target)}`;
};
